import { Fragment, useMemo } from 'react';

import { Anchor, Table, Text, Title } from '@mantine/core';

import type { BlockModel, DocxModel, InlineSegment } from '@/lib/ooxml';
import { computeListMarkers } from './listMarkers';
import classes from './DocxContent.module.css';

interface DocxBlockListProps {
  model: DocxModel;
  /** image relationship id → object URL (created by the container) */
  imageUrls: Record<string, string>;
}

/** Footnote id → display number, in order of first reference. */
function footnoteNumbering(model: DocxModel): Map<string, number> {
  const order = new Map<string, number>();
  for (const block of model.blocks) {
    for (const id of block.footnoteRefs) {
      if (!order.has(id)) {
        order.set(id, order.size + 1);
      }
    }
  }
  return order;
}

function Segments({
  segments,
  footnotes,
  imageUrls,
}: Readonly<{
  segments: InlineSegment[];
  footnotes: Map<string, number>;
  imageUrls: Record<string, string>;
}>) {
  return (
    <>
      {segments.map((seg, i) => {
        const key = `${i}`;
        switch (seg.type) {
          case 'text':
            // Direct run colour is semantic in GTI reports (RAG dots) —
            // preserve it; everything else stays normalized typography.
            return seg.color ? (
              <span key={key} style={{ color: `#${seg.color}` }}>
                {seg.text}
              </span>
            ) : (
              <Fragment key={key}>{seg.text}</Fragment>
            );
          case 'link':
            return (
              <Anchor key={key} href={seg.href} target="_blank" rel="noopener noreferrer" inherit>
                {seg.text}
              </Anchor>
            );
          case 'footnote_ref':
            return (
              <sup key={key} className={classes.footnoteRef}>
                <a href={`#docx-footnote-${seg.id}`}>[{footnotes.get(seg.id) ?? seg.id}]</a>
              </sup>
            );
          case 'image': {
            const url = imageUrls[seg.rId];
            return url ? <img key={key} src={url} alt="" className={classes.inlineImage} /> : null;
          }
          default:
            return null;
        }
      })}
    </>
  );
}

function TocBlock({ model }: Readonly<{ model: DocxModel }>) {
  // Regenerated live from the heading tree (cached page numbers in the
  // document are meaningless in a reflowing view).
  const headings = model.blocks.filter((b) => b.kind === 'heading' && b.text.trim());
  return (
    <nav className={classes.toc} aria-label="Contents">
      <Text fw={700} size="sm" c="dimmed">
        Contents
      </Text>
      {headings.map((h) => (
        <Text
          key={h.pos}
          size="sm"
          className={classes.tocEntry}
          style={{ paddingLeft: `${((h.level ?? 1) - 1) * 16}px` }}
        >
          <a href={`#docx-block-${h.pos}`}>{h.text}</a>
        </Text>
      ))}
    </nav>
  );
}

function anchorProps(block: BlockModel) {
  return {
    id: `docx-block-${block.pos}`,
    'data-pos': block.pos,
    'data-para-id': block.paraId ?? undefined,
    'data-occurrence': block.paraId ? block.occurrence : undefined,
  };
}

export function DocxBlockList({ model, imageUrls }: Readonly<DocxBlockListProps>) {
  const markers = useMemo(() => computeListMarkers(model.blocks), [model]);
  const footnotes = useMemo(() => footnoteNumbering(model), [model]);
  const referenced = Array.from(footnotes.keys()).filter((id) => model.footnotes[id]);

  return (
    <div className={classes.content}>
      {model.blocks.map((block) => {
        const common = anchorProps(block);
        switch (block.kind) {
          case 'heading': {
            const order = Math.min(Math.max(block.level ?? 1, 1), 6) as 1 | 2 | 3 | 4 | 5 | 6;
            return (
              <Title key={block.pos} order={order} className={classes.heading} {...common}>
                <Segments segments={block.segments} footnotes={footnotes} imageUrls={imageUrls} />
              </Title>
            );
          }
          case 'list_item':
            return (
              <div
                key={block.pos}
                className={classes.listItem}
                style={{ paddingLeft: `${(block.level ?? 0) * 24 + 8}px` }}
                {...common}
              >
                <span className={classes.listMarker}>{markers.get(block.pos)}</span>
                <Text span inherit>
                  <Segments segments={block.segments} footnotes={footnotes} imageUrls={imageUrls} />
                </Text>
              </div>
            );
          case 'table':
            // Wide tables scroll inside their own container rather than
            // overflowing the content column.
            return (
              <div key={block.pos} className={classes.tableScroll} {...common}>
                <Table withTableBorder withColumnBorders className={classes.table}>
                  <Table.Tbody>
                    {block.table?.rows.map((row, r) => (
                      // Grid rows are positionally stable for a parsed document.

                      <Table.Tr key={r}>
                        {row.map(
                          (cell, c) =>
                            cell.isMaster && (
                              <Table.Td
                                key={c}
                                rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                                colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}
                                data-row={r}
                                data-col={c}
                              >
                                {cell.paragraphs.map((segments, i) => (
                                  <Text key={i} size="sm">
                                    <Segments
                                      segments={segments}
                                      footnotes={footnotes}
                                      imageUrls={imageUrls}
                                    />
                                  </Text>
                                ))}
                              </Table.Td>
                            )
                        )}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            );
          case 'toc':
            return (
              <div key={block.pos} {...common}>
                <TocBlock model={model} />
              </div>
            );
          case 'image':
            return (
              <div key={block.pos} className={classes.imageBlock} {...common}>
                <Segments segments={block.segments} footnotes={footnotes} imageUrls={imageUrls} />
              </div>
            );
          case 'unsupported':
            return (
              <div key={block.pos} className={classes.unsupported} {...common}>
                <Text size="xs" c="dimmed">
                  Unsupported content ({block.unsupportedTag}) — view the Presentation tab for full
                  fidelity.
                </Text>
              </div>
            );
          case 'paragraph':
          default: {
            if (!block.text.trim() && !block.hasImage) {
              return <div key={block.pos} className={classes.emptyParagraph} {...common} />;
            }
            const body = (
              <Text
                key={block.pos}
                className={block.inTextbox ? classes.textboxParagraph : classes.paragraph}
                {...common}
              >
                <Segments segments={block.segments} footnotes={footnotes} imageUrls={imageUrls} />
              </Text>
            );
            return body;
          }
        }
      })}

      {referenced.length > 0 && (
        <div className={classes.footnotes}>
          {referenced.map((id) => (
            <Text key={id} size="xs" id={`docx-footnote-${id}`} className={classes.footnote}>
              <sup>[{footnotes.get(id)}]</sup> {model.footnotes[id]}
            </Text>
          ))}
        </div>
      )}
    </div>
  );
}
